import { useCallback } from 'react';
import { useJqlContext } from '@atlassian/jira-business-entity-common/src/controllers/jql-context/index.tsx';
import { GraphQLErrors } from '@atlassian/jira-business-graphql-errors/src/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { UNSCHEDULED_ITEMS_JQL } from '../constants.tsx';
import { QUERY } from './gql.tsx';
import type { Response } from './types.tsx';

const QUERY_NAME = 'JWMCalendarUnscheduledItemsCount';

type FetchCount = () => Promise<number>;

export const useUnscheduledItemsCount = (): FetchCount => {
	const { cloudId } = useTenantContext();
	const jqlContext = useJqlContext();

	return useCallback(async () => {
		try {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const response = (await performPostRequest(`/rest/gira/1/?operation=${QUERY_NAME}`, {
				body: JSON.stringify({
					query: QUERY,
					variables: {
						cloudId,
						jql: `${jqlContext} AND ${UNSCHEDULED_ITEMS_JQL}`,
					},
				}),
			})) as Response;

			if (response.errors != null) {
				throw new GraphQLErrors(response.errors);
			}

			if (response.data == null) {
				throw new Error('GraphQL response has no data');
			}

			return response.data.jira.unscheduledCount?.totalCount ?? 0;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (restError: any) {
			fireErrorAnalytics({
				meta: {
					id: QUERY_NAME,
					packageName: 'jiraBusinessCalendar',
					teamName: 'jira-yurree',
				},
				error: restError,
				sendToPrivacyUnsafeSplunk: true,
			});

			throw restError;
		}
	}, [cloudId, jqlContext]);
};
